import { useEffect, useMemo, useRef, useState } from "react"
import { toast } from "react-toastify"

import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import {
  useLazyGetEnterpriseAlertsQuery,
  useLazyGetEnterpriseAlertStatusesWidgetQuery,
  useLazyGetEnterpriseAlertTypesWidgetQuery,
} from "store/api/enterpriseApi"
import { useGetEnterpriseDashboardTotalStatsQuery } from "store/api/widgetsApi"
import { ApiTypes, UITypes } from "types"

import { convertEnterpriseAlertsToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { AlertsActions } from "components/common/AlertsActions"
import { BarChart } from "components/common/BarChart"
import { ContentWrapper } from "components/common/ContentWrapper"
import { DateRangeTabsFilter } from "components/common/DateRangeTabsFilter"
import { getDateRangeParams } from "components/common/DateRangeTabsFilter/dateRangeFilterUtils"
import ExportButton from "components/common/ExportButton"
import { Pagination } from "components/common/Pagination"
import { Spinner } from "components/common/Spinner"
import EditColumnsButton from "components/EditColumnsButton"
import TableWrapper from "components/table/TableWrapper"

import { enterpriseAlertsTableHeaderCells } from "./enterpriseAlertsTableHeaderCells"

import styles from "./index.module.scss"

const TABLE_NAME = "contractor_alerts_list"
const DATE_PARAM_NAME = "date"

const emptyAlertSummary = {
  totalEnrolledThermostats: 0,
  devicesWithActiveAlerts: 0,
  alertsCount: 0,
}

const emptyAlertTypes = {
  systemEfficiency: 0,
  airQuality: 0,
  ambientSensorMalfunction: 0,
  fanMalfunction: 0,
  humiditySensorMalfunction: 0,
  temperatureSensorMalfunction: 0,
}

const emptyAlertStatuses = {
  active: 0,
  frozen: 0,
  acknowledged: 0,
}

const EnterpriseAlertsSummaryWidget = ({ updateIndex }: { updateIndex: number }) => {
  const { data: totalStatsResponse, isFetching: isTotalStatsFetching } = useGetEnterpriseDashboardTotalStatsQuery()
  const [triggerGetAlertsTypeWidget, { isFetching: isTypesFetching }] = useLazyGetEnterpriseAlertTypesWidgetQuery()
  const [triggerGetStatusesWidget, { isFetching: isStatusesFetching }] = useLazyGetEnterpriseAlertStatusesWidgetQuery()
  const [summary, setSummary] = useState(emptyAlertSummary)
  const [alertTypes, setAlertTypes] = useState(emptyAlertTypes)
  const { urlQueryParams } = useUrlQueryParams()

  useEffect(() => {
    const fetchTypes = async () => {
      const res = await triggerGetAlertsTypeWidget({
        date_start: urlQueryParams.date_start,
        date_end: urlQueryParams.date_end,
      })

      const widgetData = res?.data?.data

      if (widgetData) {
        setAlertTypes({
          systemEfficiency: widgetData.efficiency_issue ?? 0,
          airQuality: widgetData.air_quality ?? 0,
          ambientSensorMalfunction: widgetData.ambient_sensor_malfunction ?? 0,
          fanMalfunction: widgetData.fan_malfunction ?? 0,
          humiditySensorMalfunction: widgetData.humidity_sensor_malfunction ?? 0,
          temperatureSensorMalfunction: widgetData.temperature_sensor_malfunction ?? 0,
        })
        setSummary(current => ({
          ...current,
          devicesWithActiveAlerts: widgetData.devices_with_active_alerts ?? 0,
        }))
      }
    }

    fetchTypes()
  }, [triggerGetAlertsTypeWidget, updateIndex, urlQueryParams.date_end, urlQueryParams.date_start])

  useEffect(() => {
    const fetchStatuses = async () => {
      const res = await triggerGetStatusesWidget({
        date_start: urlQueryParams.date_start,
        date_end: urlQueryParams.date_end,
      })

      const widgetData = res?.data?.data

      if (widgetData) {
        setSummary(current => ({
          ...current,
          alertsCount: widgetData.alerts_count ?? 0,
        }))
      }
    }

    fetchStatuses()
  }, [triggerGetStatusesWidget, updateIndex, urlQueryParams.date_end, urlQueryParams.date_start])

  useEffect(() => {
    setSummary(current => ({
      ...current,
      totalEnrolledThermostats: totalStatsResponse?.data?.thermostats ?? 0,
    }))
  }, [totalStatsResponse])

  return (
    <section className={styles.widgetWrapper}>
      <header className={styles.widgetHeader}>
        <h3 className={styles.widgetTitle}>Alerts</h3>
        <div>
          <span className={styles.summaryIndicators}>
            <i>{summary.totalEnrolledThermostats}</i> Total Enrolled Thermostats
          </span>
          <span className={styles.summaryIndicators}>
            <i>{summary.devicesWithActiveAlerts}</i> Thermostats With Alerts
          </span>
          <span className={styles.summaryIndicators}>
            <i>{summary.alertsCount}</i> Alerts
          </span>
        </div>
      </header>
      <div className={styles.widgetContent}>
        <div className={styles.alertStatsGrid}>
          <div className={styles.alertStat}>
            <span>System Efficiency</span>
            <strong>{alertTypes.systemEfficiency}</strong>
          </div>
          <div className={styles.alertStat}>
            <span>Air Quality Sensor Malfunction</span>
            <strong>{alertTypes.airQuality}</strong>
          </div>
          <div className={styles.alertStat}>
            <span>Ambient Sensor Malfunction</span>
            <strong>{alertTypes.ambientSensorMalfunction}</strong>
          </div>
          <div className={styles.alertStat}>
            <span>Fan Malfunction</span>
            <strong>{alertTypes.fanMalfunction}</strong>
          </div>
          <div className={styles.alertStat}>
            <span>Humidity Sensor Malfunction</span>
            <strong>{alertTypes.humiditySensorMalfunction}</strong>
          </div>
          <div className={styles.alertStat}>
            <span>Temperature Sensor Malfunction</span>
            <strong>{alertTypes.temperatureSensorMalfunction}</strong>
          </div>
        </div>
        {(isTotalStatsFetching || isTypesFetching || isStatusesFetching) && (
          <div className={styles.widgetLoader}>
            <Spinner />
          </div>
        )}
      </div>
    </section>
  )
}

const EnterpriseAlertStatusesWidget = ({ updateIndex }: { updateIndex: number }) => {
  const [triggerGetStatusesWidget, { isFetching, data }] = useLazyGetEnterpriseAlertStatusesWidgetQuery()
  const [statuses, setStatuses] = useState(emptyAlertStatuses)
  const { urlQueryParams } = useUrlQueryParams()

  useEffect(() => {
    triggerGetStatusesWidget({
      date_start: urlQueryParams.date_start,
      date_end: urlQueryParams.date_end,
    })
  }, [triggerGetStatusesWidget, updateIndex, urlQueryParams.date_end, urlQueryParams.date_start])

  useEffect(() => {
    if (data?.data) {
      setStatuses({
        active: data.data.active ?? 0,
        frozen: data.data.frozen ?? 0,
        acknowledged: data.data.acknowledged ?? 0,
      })
    }
  }, [data])

  return (
    <section className={styles.widgetWrapper}>
      <header className={styles.widgetHeader}>
        <h3 className={styles.widgetTitle}>Alerts Statuses</h3>
      </header>
      <div className={styles.widgetContent}>
        <div className={styles.chartWrapper}>
          <BarChart
            items={[
              { color: "#22C55E", value: statuses.active },
              { color: "#475569", value: statuses.frozen },
              { color: "#2DB2C8", value: statuses.acknowledged },
            ]}
          />
          <div className={styles.indicatorsWrapper}>
            <span className={styles.indicator}>
              <i className={styles.colorCircle} style={{ backgroundColor: "#22C55E" }} />
              Active
              <i>{statuses.active}</i>
            </span>
            <span className={styles.indicator}>
              <i className={styles.colorCircle} style={{ backgroundColor: "#475569" }} />
              Frozen
              <i>{statuses.frozen}</i>
            </span>
            <span className={styles.indicator}>
              <i className={styles.colorCircle} style={{ backgroundColor: "#2DB2C8" }} />
              Acknowledged
              <i>{statuses.acknowledged}</i>
            </span>
          </div>
        </div>
        {isFetching && (
          <div className={styles.widgetLoader}>
            <Spinner />
          </div>
        )}
      </div>
    </section>
  )
}

export const EnterpriseAlertsPage = () => {
  const [triggerGetAlerts, { isFetching, data }] = useLazyGetEnterpriseAlertsQuery()
  const [triggerGetTableColumns, { data: columnsData, isFetching: isColumnsFetching }] = useLazyGetTableColumnsQuery()
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const [selectedAlerts, setSelectedAlerts] = useState<ApiTypes.Model.EnterpriseAlert[]>([])
  const [updateIndex, setUpdateIndex] = useState(0)
  const isFirstRunRef = useRef(true)

  const alertsList = data?.data ?? []
  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(enterpriseAlertsTableHeaderCells, columnsData.data.config)
        : enterpriseAlertsTableHeaderCells,
    [columnsData],
  )
  const isHeaderChecked = useMemo(() => {
    if (!alertsList.length) return false
    return alertsList.every(alert => selectedAlerts.some(s => s.id === alert.id))
  }, [alertsList, selectedAlerts])

  const { handleExport } = useExportCSV({
    params: urlQueryParams,
    headerCells: tableHeaderCells,
    meta: data?.meta,
    fileName: "enterprise-alerts",
    convertData: convertEnterpriseAlertsToTableRows,
    useLazyQuery: useLazyGetEnterpriseAlertsQuery,
  })

  useEffect(() => {
    const startKey = `${DATE_PARAM_NAME}_start`
    const endKey = `${DATE_PARAM_NAME}_end`
    if (!urlQueryParams[startKey] && !urlQueryParams[endKey]) {
      setUrlQueryParams(getDateRangeParams("last-7-days", DATE_PARAM_NAME))
    }
  }, [])

  useEffect(() => {
    triggerGetTableColumns({ list_name: TABLE_NAME })
  }, [triggerGetTableColumns])

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetAlerts(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (isFirstRunRef.current) {
      isFirstRunRef.current = false
      const startKey = `${DATE_PARAM_NAME}_start`
      const endKey = `${DATE_PARAM_NAME}_end`
      if (!urlQueryParams[startKey] && !urlQueryParams[endKey]) {
        return
      }
    }
    sendRequest(urlQueryParams)
  }, [urlQueryParams])

  const onSelectAlert = (id: number) => {
    setSelectedAlerts(current =>
      current.some(item => item.id === id)
        ? current.filter(item => item.id !== id)
        : [...current, alertsList.find(item => item.id === id)!],
    )
  }

  const toggleAllAlerts = () => {
    if (isHeaderChecked) {
      const currentPageAlertIds = alertsList.map(a => a.id)
      setSelectedAlerts(prev => prev.filter(a => !currentPageAlertIds.includes(a.id)))
      return
    }

    setSelectedAlerts(prev => {
      const existingIds = new Set(prev.map(a => a.id))
      const toAdd = alertsList.filter(a => !existingIds.has(a.id))
      return [...prev, ...toAdd]
    })
  }

  const handleSuccess = () => {
    setSelectedAlerts([])
    setUpdateIndex(current => current + 1)
    sendRequest(urlQueryParams)
  }

  return (
    <main className={styles.page}>
      <div className={styles.widgetsWrapper}>
        <EnterpriseAlertsSummaryWidget updateIndex={updateIndex} />
        <EnterpriseAlertStatusesWidget updateIndex={updateIndex} />
      </div>
      <ContentWrapper title="Alerts History">
        <nav className={styles.tableNavWrapper}>
          <div className={styles.navRow}>
            <div className={styles.dateFilterContainer}>
              <DateRangeTabsFilter
                paramName={DATE_PARAM_NAME}
                urlQueryParams={urlQueryParams}
                onParamsChange={setUrlQueryParams}
              />
            </div>
            <div className={styles.buttonsContainer}>
              <EditColumnsButton tableName={TABLE_NAME} tableHeaderCells={tableHeaderCells} />
              <ExportButton onClick={handleExport} disabled={data?.meta?.total === 0} />
            </div>
          </div>
          <div className={styles.alertActionsContainer}>
            <AlertsActions isVendor selectedAlerts={selectedAlerts} onSuccess={handleSuccess} />
          </div>
        </nav>
        <TableWrapper
          headerCells={tableHeaderCells}
          columnsLoaded={!isColumnsFetching}
          data={convertEnterpriseAlertsToTableRows(alertsList)}
          isLoading={isFetching}
          onCheckboxChange={onSelectAlert}
          currentItemIDs={selectedAlerts.map(item => item.id)}
          onHeaderCheckboxChange={toggleAllAlerts}
          isHeaderChecked={isHeaderChecked}
          skippedTags={[DATE_PARAM_NAME]}
          additionalTags={
            selectedAlerts.length
              ? [
                  {
                    label: "Selected Alerts",
                    values: [selectedAlerts.length],
                  },
                ]
              : []
          }
          clearAdditionalTags={() => setSelectedAlerts([])}
        />
        <Pagination meta={data?.meta} />
      </ContentWrapper>
    </main>
  )
}

export default EnterpriseAlertsPage
